<template>
  <!-- prettier-ignore -->
  <div class="TabsContainer">
    <horizontal-scroller
      class="tabs-container"
      :by-index="selectedIndex"
      @select-next="selectNext"
      @select-prev="selectPrev"
    >
      <template #items>
        <a
          v-for="(_, category) in $props.productsTree"
          :key="category"
          class="btn"
          :class="{ 'btn--selected': category === activeCategory }"
          @click="setActiveCategory(category)"
          v-html="$t(`content.categories.${category}.title`)"
        />
      </template>
    </horizontal-scroller>
    <div
      id="tabsContent"
      ref="tabsContentRef"
      class="tabs-content"
      @scroll="handleScroll"
    >
      <div
        v-for="product in products"
        :key="product.id"
        class="tabs-product-detail"
        :class="{ 'with-person-link': itemData.isPersonDataEdited }"
      >
        <product-detail
          class="product"
          :basket="itemData.basket"
          :labels="$t(`content.products.${product.productId}`)"
          :package-id="product.packageId"
          :product="product"
        />
      </div>
    </div>
    <div
      v-if="hasOverflow"
      class="overflow-gradient"
    />
  </div>
</template>

<script setup>
import { computed, inject, onMounted, onUpdated, ref } from 'vue'

import usePartnerWidgetBundleLoader from '@/hooks/usePartnerWidgetBundleLoader'

import HorizontalScroller from '@/components/Container/HorizontalScroller'
import ProductDetail from '@/modules/PartnerWidget/components/product/ProductDetail'

// HOOKS
const { bundleConfig } = usePartnerWidgetBundleLoader()

// INIT
const { itemData } = inject('detailProps')
const emit = defineEmits(['opened'])
const props = defineProps({
  productsTree: {
    type: Object,
    default: () => ({}),
  },
})

// DATA
const activeCategory = ref(null)
const tabsContentRef = ref(null)
const hasOverflow = ref(false)

// COMPUTED
const products = computed(() => {
  if (!activeCategory.value) return []
  return props.productsTree[activeCategory.value]
})

const allCategories = computed(() => {
  return Object.keys(props.productsTree)
})

const selectedIndex = computed(() => {
  return allCategories.value.findIndex(x => x === activeCategory.value)
})

// METHODS
function setActiveCategory(category, emitOpened = true) {
  activeCategory.value = category
  if (emitOpened) {
    emit('opened', category)
  }
}

function selectNext() {
  setActiveCategory(allCategories.value[selectedIndex.value + 1], true)
}

function selectPrev() {
  setActiveCategory(allCategories.value[selectedIndex.value - 1], true)
}

function handleScroll() {
  const content = tabsContentRef.value
  const hasVerticalOverflow = content.scrollHeight > content.clientHeight
  if (content && hasVerticalOverflow) {
    const hasScrolledToBottom = content.scrollTop === content.scrollHeight - content.offsetHeight
    hasOverflow.value = !hasScrolledToBottom
  }
}

// LIFECYCLE HOOKS
onMounted(() => {
  const emitOpen = !(bundleConfig.skipPackagesOverview && bundleConfig.skipEntryPage)
  setActiveCategory(Object.keys(props.productsTree)[0], emitOpen)
})

onUpdated(() => {
  handleScroll()
})
</script>

<style scoped>
/* settings for horizontal-scroller .overflow */
.TabsContainer {
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;

  :deep(.overflow) {
    border-radius: var(--fixed-border-radius-fix-full);
    background: var(--surface-ext-b-low);
    padding: var(--fixed-spacing-fix-01);
    gap: var(--fixed-spacing-fix-04);
    width: 100%;

    .theme-darkgreen & {
      background: var(--surface-ext-b-medium);
    }
  }

  .btn {
    transition-property: background, box-shadow, text-decoration;
    transition-duration: 150ms;
    transition-timing-function: linear;
    flex: 1 0 auto;

    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;

    font-weight: var(--font-weight-light);
    padding: var(--dotcom-responsive-spacing-res-xxxs) var(--fixed-spacing-fix-04);
    border-radius: var(--fixed-border-radius-fix-full);

    &:hover {
      background: var(--surface-ext-b-medium);
      .theme-darkgreen & {
        background: var(--surface-ext-b-low);
      }
    }

    &:focus {
      background: var(--light--surface-ext-b-medium);
      .theme-darkgreen & {
        background: var(--surface-ext-b-low);
      }
    }

    &:active {
      background: var(--surface-ext-b-high) !important;
      .theme-darkgreen & {
        background: var(--surface-ext-b-high) !important;
      }
    }

    &--selected {
      background: var(--surface);
      box-shadow: var(--elevation-level-2);
      font-weight: var(--font-weight-regular);
    }

    &--unavailable {
      position: relative;
      text-decoration: line-through;
      cursor: not-allowed;
      pointer-events: none;
    }
  }

  :deep(.control) {
    width: var(--fixed-spacing-fix-08);
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: center;
    border-radius: var(--fixed-border-radius-fix-full);
    background: var(--surface-ext-b-low);

    .theme-darkgreen & {
      background: var(--surface-ext-b-medium);
    }

    &:hover {
      background: var(--surface-ext-b-medium);
      .theme-darkgreen & {
        background: var(--surface-ext-b-high);
      }
    }

    &:focus {
      background: var(--light--surface-ext-b-medium);
      .theme-darkgreen & {
        background: var(--surface-ext-b-low);
      }
    }

    &:active {
      background: var(--surface-ext-b-high) !important;
    }

    svg {
      width: 32px;
      color: var(--Interaction-States-enabled-extended);
      font-size: 16px;
      font-weight: normal;
      text-align: center;
    }
  }

  .tabs-content {
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: auto;
    max-height: 360px;
    height: 100vw;
    margin-top: var(--fixed-spacing-fix-02);
    .tabs-product-detail {
      background: var(--surface);
      flex: 1 0 auto;
      overflow: auto;
      padding: var(--fixed-spacing-fix-04);
      margin: var(--fixed-spacing-fix-02);
      max-height: 340px;
      border-radius: var(--fixed-border-radius-fix-03);
      box-shadow: var(--elevation-level-2);

      &.with-person-link {
        max-height: 323px;
      }

      .product:not(:last-of-type) {
        border-bottom: 1px solid var(--surface-dim);
      }
    }

    &::-webkit-scrollbar {
      width: 8px;
      max-height: 8px;
      padding: var(--fixed-spacing-fix-02) 0 var(--fixed-spacing-fix-02) var(--fixed-spacing-fix-03);
    }

    &::-webkit-scrollbar-thumb {
      background: var(--on-surface-medium);
      border-radius: 100px;
    }
  }

  .overflow-gradient {
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 100px;
      width: 98%;
      background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
      .theme-darkgreen & {
        background: linear-gradient(180deg, rgba(235, 253, 237, 0) 0%, var(--surface-ext-b-low) 100%);
      }
    }
  }
}

@media (--v-large) {
  .TabsContainer {
    .btn {
      height: 56px;
    }

    :deep(.control) {
      height: 65px;
    }
  }
}

@media (--v-wide) {
  .TabsContainer {
    .btn {
      height: 60px;
    }

    :deep(.control) {
      height: 70px;
    }
  }
}
</style>
<style name="short" scoped>
@media screen and (max-height: 596px) {
  .TabsContainer {
    .tabs-product-detail {
      max-height: none;

      &.with-person-link {
        max-height: none;
      }
    }
  }
}
</style>
